import { v4 as uuidv4 } from 'uuid';
import { getLocalStorage, storeLocalStorage } from './common';

const STORAGE_KEY = "filterPanels";

let panelState: Record<string, boolean> | null = null;

function readPanelState(): Record<string, boolean> {
	if (panelState) return panelState;

	panelState = {};

	try {
		const raw = getLocalStorage(STORAGE_KEY);
		const parsed = raw ? JSON.parse(raw) : null;

		if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
			panelState = parsed as Record<string, boolean>;
		}
	} catch (e) {
		console.warn("Could not read filter panel state " + e);
	}

	return panelState;
}

function writePanelState(key: string, open: boolean): void {
	const state = readPanelState();
	state[key] = open;
	storeLocalStorage(STORAGE_KEY, JSON.stringify(state));
}

export class FilterPanel extends HTMLElement {
	static get observedAttributes(): string[] {
		return ["collapsed", "open"];
	}

	public summaryProvider: (() => FilterSummaryItem[]) | null = null;

	private toggleButton: HTMLButtonElement | null = null;
	private headerElement: HTMLDivElement | null = null;
	private bodyElement: HTMLDivElement | null = null;
	private summaryElement: HTMLDivElement | null = null;
	private bodyId: string;
	private initialised = false;

	constructor() {
		super();
		this.bodyId = `filter-panel-body-${uuidv4()}`;
	}

	connectedCallback(): void {
		if (!this.initialised) {
			this.render();
			this.initialised = true;
		}

		this.cacheElements();
		this.headerElement?.addEventListener("click", this.onToggleClick);

		const key = this.persistKey;
		const stored = key ? readPanelState()[key] : undefined;

		if (typeof stored === "boolean") {
			this.open = stored;
		} else if (!this.hasAttribute("collapsed") && !this.hasAttribute("open")) {
			// If no state attributes are set, default to open.
			this.open = true;
		} else {
			this.syncState();
		}
	}

	disconnectedCallback(): void {
		this.headerElement?.removeEventListener("click", this.onToggleClick);
	}

	attributeChangedCallback(): void {
		this.syncState();
	}

	get open(): boolean {
		return !this.hasAttribute("collapsed");
	}

	set open(value: boolean) {
		if (value) {
			this.removeAttribute("collapsed");
			this.setAttribute("open", "");
		} else {
			this.setAttribute("collapsed", "");
			this.removeAttribute("open");
		}

		this.syncState();
	}

	public expand(): void {
		this.open = true;
	}

	public collapse(): void {
		this.open = false;
	}

	public toggle(force?: boolean): void {
		this.open = typeof force === "boolean" ? force : !this.open;

		const key = this.persistKey;
		if (key) writePanelState(key, this.open);

		this.dispatchEvent(
			new CustomEvent<{ open: boolean }>("filter-panel-toggle", {
				bubbles: true,
				detail: { open: this.open },
			}),
		);
	}

	private onToggleClick = (): void => {
		this.toggle();
	};

	private get persistKey(): string | null {
		if (!this.hasAttribute("persist")) return null;

		return this.getAttribute("data-panel-key");
	}

	private cacheElements(): void {
		this.toggleButton = this.querySelector<HTMLButtonElement>("[data-filter-panel-toggle]");
		this.headerElement = this.querySelector<HTMLDivElement>("[data-filter-panel-header]");
		this.bodyElement = this.querySelector<HTMLDivElement>("[data-filter-panel-body]");
		this.summaryElement = this.querySelector<HTMLDivElement>("[data-filter-panel-summary]");
	}

	set summary(items: FilterSummaryItem[]) {
		if (!this.summaryElement) return;

		const spans = items.map(item => {
			const span = document.createElement("span");
			span.textContent = item.text;
			if (item.className) span.className = item.className;
			if (item.title) span.title = item.title;
			return span;
		});

		this.summaryElement.replaceChildren(...spans);
		this.summaryElement.hidden = spans.length === 0;
	}

	public refreshSummary(): void {
		if (this.summaryProvider) this.summary = this.summaryProvider();
	}

	private syncState(): void {
		if (!this.toggleButton || !this.bodyElement) return;

		const expanded = this.open;

		this.toggleButton.setAttribute("aria-expanded", String(expanded));
		this.bodyElement.hidden = !expanded;
		this.bodyElement.inert = !expanded;
	}

	private render(): void {
		// Save the html content to re-append later, as we will be restructuring the DOM
		const originalChildren = Array.from(this.childNodes);

		const headerNodes: Node[] = [];
		const bodyNodes: Node[] = [];

		// Separate header and body nodes based on data-role attribute
		for (const node of originalChildren) {
			if (
				node instanceof HTMLElement &&
				node.matches("[data-role='header']")
			) {
				headerNodes.push(node);
			} else {
				bodyNodes.push(node);
			}
		}

		const header = document.createElement("div");
		header.classList.add("filter-panel__header", "nowrap");
		header.setAttribute("data-filter-panel-header", "");

		const headerMain = document.createElement("div");
		headerMain.className = "filter-panel__header-main";
		headerMain.setAttribute("data-filter-panel-header-main", "");

		for (const node of headerNodes) {
			if (node instanceof HTMLElement) {
				node.removeAttribute("data-role");
			}
			headerMain.appendChild(node);
		}

		const toggle = document.createElement("div");
		toggle.className = "filter-panel__toggle";
		toggle.setAttribute("data-filter-panel-toggle", "");
		toggle.setAttribute("aria-controls", this.bodyId);
		toggle.setAttribute("aria-expanded", "true");
		toggle.setAttribute("aria-label", "Toggle filter section");

		toggle.innerHTML = `
			<svg class="icon chevron">
				<use href="#icon-chevron">
			</svg>
		`;

		const summary = document.createElement("div");
		summary.className = "filter-panel__summary";
		summary.setAttribute("data-filter-panel-summary", "");
		summary.hidden = true;

		header.append(headerMain, summary, toggle);

		const body = document.createElement("div");
		body.className = "filter-panel__body";
		body.id = this.bodyId;
		body.setAttribute("data-filter-panel-body", "");

		for (const node of bodyNodes) {
			body.appendChild(node);
		}

		this.replaceChildren(header, body);
	}
}


customElements.define("filter-panel", FilterPanel);